import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Ytd } from '@app/models/payroll/ytd.model';
import { HandsOnTableView } from '@app/modules/common/views';
import { PayrollResources } from '@app/modules/payroll/payroll.resources';
import { PayrollModuleMenuService } from '@app/modules/payroll/services/payroll-module-menu.service';
import { PayrollService, PayrollSettingsService } from '@app/services';
import { AuthService } from '@app/services/auth.service';
import { NotifyService } from '@app/services/notify.service';
import { CellChange, RowObject } from 'handsontable/common';
import { ColumnSettings } from 'handsontable/settings';
import { HandsontableableComponent } from '../../utils/ytds/handsontable-helpers';
import { Datum } from '../../utils/ytds/ytd-table-helpers';

@Component({
    templateUrl: './vacation-ytd-index.template.html',
    styleUrls: ['./vacation-ytd-index.style.scss'],
})
export class VacationYtdIndexView extends HandsOnTableView implements OnInit, HandsontableableComponent {
    isLoading = true;
    isTimeOffYtdUpdatable = false;
    timeOffTableColumnName = 'time_off_onboarding_ytd_balance';
    dismissedYTDUpdatableWarning = false;
    ytds: Ytd[] = [];
    hasEmployeesOnPayroll = true;

    constructor(
        private notify: NotifyService,
        private auth: AuthService,
        private http: HttpClient,
        private payrollService: PayrollService,
        private payrollSettingsService: PayrollSettingsService,
        public payrollModuleMenuService: PayrollModuleMenuService
    ) {
        super();
        this.setHotId('vacationYtdTable');
        this.translateService.onLangChange.pipe(takeUntilDestroyed()).subscribe(() => {
            this.isLoading = true;
            this.loadData();
        });
    }

    async ngOnInit(): Promise<void> {
        try {
            this.loadData();
        } catch (err) {
            this.notify.error(err || 'payroll.vacation-ytds.couldNotLoadEmployeeVacationYtds');
        }

        await this.payrollSettingsService.load();
        this.setTemplateListeners(this);
    }

    ngOnDestroy(): void {
        this.removeWindowEventListeners();
    }

    /**
     * We need to load a lot of data to make this table work
     * Dependant calls are ordered using async await, and we call independent calls at the same time
     */
    async loadData(): Promise<void> {
        const [isTimeOffYtdUpdatable] = await Promise.all([
            this.payrollService.getIsTimeOffYTDUpdatable(this.auth.company.id),
            this.setVacationYtds(),
        ]);

        this.isTimeOffYtdUpdatable = isTimeOffYtdUpdatable;
        this.rowHeaders = this.ytds.map((ytd) => ytd.fullNameInReverseOrder);
        this.setRowHeaderWidth();
        this.hasEmployeesOnPayroll = !!this.ytds.length;
        this.columns = this.buildVacationYTDColumns();
        this.dataset = this.buildVacationYTDDataset();
        this.isLoading = false;
    }

    async setVacationYtds(): Promise<void> {
        [this.ytds] = await Ytd.with(this.timeOffTableColumnName).all();
    }

    buildVacationYTDColumns(): ColumnSettings[] {
        const vacationBalanceYTD = {
            data: this.timeOffTableColumnName,
            title: this.translateService.instant('payroll.vacation-ytds.vacationBalance'),
            type: 'numeric',
        };

        return [vacationBalanceYTD];
    }

    async saveChange(change: CellChange): Promise<any> {
        const [index, , previousValue, newValue] = change as [number, string, number, number];
        if (previousValue === newValue) {
            return;
        }
        const employee = this.ytds[index];
        const vacationPayYtdPayload = {
            data: {
                type: 'time_off_ytds',
                attributes: {
                    ytd: newValue,
                },
            },
        };

        try {
            await this.http
                .put(
                    PayrollResources.VacationYTD.replace(':employee', employee.hrEmployeeId.toString()),
                    vacationPayYtdPayload
                )
                .toPromise();
        } catch {
            this.onVacationYTDSaveFailure(change);
        }
    }

    onVacationYTDSaveFailure(change: CellChange): void {
        const [index, column] = change;
        this.highlightError(index, column as string);
    }

    buildVacationYTDDataset(): Datum[] {
        return this.ytds.map((ytd: Ytd) =>
            this.columns.reduce((acc: RowObject, column: ColumnSettings) => {
                // for each column inside each YTD (one YTD per employee)

                const columnName = column.data as string;

                if (columnName === this.timeOffTableColumnName) {
                    acc[columnName] = ytd.timeOffOnboardingYtdBalance;
                    return acc;
                }

                return acc;
            }, {})
        );
    }
}
